import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import moment from 'moment';

//Components
import { Tooltip } from '@gpstrackit/react-controls';
import InfoIcon from '@mui/icons-material/Info';
import DataView from 'Components/DataView';
import {
    Grid,
    Container,
    Paper,
    Typography,
    Divider,
    IconButton,
    Icon,
} from "@mui/material";

//Actions
import { scheduledReportsOpenModal, scheduledReportsContactsOpenModal, clearRedux, downloadReportFileRequest } from 'Redux/actions';

//Helpers
import { downloadFile } from 'Utils/helpers';

//Views
import AdvancedFilters from 'Modules/reports/.components/scheduledReports/.components/advancedFilters';
import Details from 'Modules/accounts/.components/scheduledReports/.components/details';
import Contacts from 'Modules/accounts/.components/scheduledReports/.components/contacts';

const REPORT_RETENTION_DAYS = 30;
const getRetentionMinDate = () => moment().subtract(REPORT_RETENTION_DAYS, 'days').startOf('day');

const ScheduledReports = (props) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const navigate = useNavigate();
    const tableRef = useRef(null);

    //Redux States
    const { loading, openModal, contactsOpenModal } = useSelector(state => state.scheduledReportsRedux);
    const lang = useSelector(state => state.commonRedux.language);
    const downloadReportFileResult = useSelector(state => state.generalRedux.downloadReportFileResult);

    //Local States
    const [openPanel, setOpenPanel] = useState(false);
    const [account, setAccount] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        if (downloadReportFileResult && downloadReportFileResult?.url) {
            downloadFile(downloadReportFileResult?.url)
            dispatch(clearRedux("GENERAL", { downloadReportFileResult: null }))
        }
    }, [downloadReportFileResult])

    const [columns, setColumns] = useState([
        {
            key: "url",
            field: "outputUrl",
            title: "",
            show: true,
            width: 40,
            icon: true,
            render: (item) => {
                let url = null
                try {
                    url = JSON.parse(item?.outputUrl)
                    url = url[0]?.url
                } catch (error) { }
                return (
                    <>
                        {
                            url ?
                                <Tooltip title={messages['scheduled_download_report']}>
                                    <a href={"#"} onClick={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault();
                                    }}>
                                        <Icon style={{ paddingTop: 3, cursor: 'pointer', color: '#000' }} onClick={() => url && dispatch(downloadReportFileRequest({ id: url }))}>get_app</Icon>
                                    </a>
                                </Tooltip>
                                :
                                <Tooltip title={messages['scheduled_no_data_available']}>
                                    <Icon style={{ paddingTop: 3, opacity: '0.5', color: '#000' }}>get_app</Icon>
                                </Tooltip>


                        }
                    </>
                )
            }
        },
        {
            key: "params",
            field: "params",
            title: messages['scheduled_reports_params'],
            show: true,
            width: 80,
            code: "scheduled_reports_params",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title={messages['scheduled_show_params']}>
                            <InfoIcon style={{ cursor: 'pointer' }} onClick={() => dispatch(scheduledReportsOpenModal(true, item.params))} />
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: "company_name",
            title: messages['scheduled_account'],
            show: true,
            width: 200,
            code: "scheduled_account",
            functionString: `return row?.company?.name`,
            export: true,
            render: (item) => {
                return (
                    <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.company?.name}</span>
                    </NavLink>
                )
            }
        },

        {
            key: "reportName",
            field: "reportName",
            title: messages['scheduled_reports_report_name'],
            sort: true,
            show: true,
            resize: true,
            width: 250,
            export: true,
            code: "scheduled_reports_report_name",
            render: (item) => <span>{item?.reportName}</span>
        },
        {
            key: "reportType",
            field: "reportType",
            sort: true,
            title: messages['scheduled_reports_report_type'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            code: "scheduled_reports_report_type",
            render: (item) => <span>{item?.reportType}</span>
        },
        {
            key: "createdOn",
            field: "createdOn",
            sort: true,
            title: messages['scheduled_created_on'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "scheduled_created_on",
            render: (item) => <span>{item?.createdOn ? moment(item?.createdOn).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "lastProcessUpdate",
            field: "lastProcessUpdate",
            title: messages['scheduled_last_process_update'],
            sort: true,
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.lastProcessUpdate ? formatDate(row?.lastProcessUpdate) : ''
            `,
            code: "scheduled_last_process_update",
            render: (item) => <span>{item?.lastProcessUpdate ? moment(item?.lastProcessUpdate).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "scheduledAt",
            field: "scheduledAt",
            sort: true,
            title: messages['scheduled_reports_scheduled_at'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.scheduledAt ? formatDate(row?.scheduledAt) : ''
            `,
            code: "scheduled_reports_scheduled_at",
            render: (item) => <span>{item?.scheduledAt ? moment(item?.scheduledAt).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "completedOn",
            field: "completedOn",
            sort: true,
            title: messages['scheduled_reports_completed_on'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.completedOn ? formatDate(row?.completedOn) : ''
            `,
            code: "scheduled_reports_completed_on",
            render: (item) => <span>{item?.completedOn ? moment(item?.completedOn).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "format",
            field: "format",
            title: messages['scheduled_reports_format'],
            sort: true,
            show: true,
            resize: true,
            width: 200,
            export: true,
            code: "scheduled_reports_format",
            render: (item) => <span>{item?.format}</span>
        },
        {
            key: "sendByEmail",
            field: "sendByEmail",
            title: messages['scheduled_reports_send_by_email'],
            sort: true,
            show: true,
            resize: true,
            width: 200,
            export: true,
            code: "scheduled_reports_send_by_email",
            functionString: `
                let lang = "${lang}";
                const translations = {
                    en: {
                        yes: "Yes",
                        no: "No",   
                    },
                    es: {
                        yes: "Si",
                        no: "No",
                    }
                };
                return row?.sendByEmail ? translations[lang].yes : translations[lang].no
            `,
            render: (item) => <span>{item?.sendByEmail ? messages['scheduled_reports_yes'] : messages['scheduled_reports_no']}</span>
        },
        {
            key: "emailsTo",
            field: "emailsTo",
            title: messages['scheduled_reports_emails'],
            show: true,
            resize: true,
            width: 100,
            export: true,
            code: "scheduled_reports_emails",
            functionString: `
                let emailsTo = []
                try {
                    emailsTo = JSON.parse(JSON.parse(row?.emailsTo))
                } catch(error){}
                return emailsTo?.length ? emailsTo : null
            `,
            render: (item) => {
                let emailsTo = []
                try {
                    emailsTo = JSON.parse(JSON.parse(item?.emailsTo))
                } catch (error) { }
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {emailsTo && emailsTo?.length
                            ? <Icon style={{ cursor: 'pointer' }} onClick={() => dispatch(scheduledReportsContactsOpenModal(true, emailsTo))}>people</Icon>
                            : <></>
                        }
                    </div>
                )
            }
        },
        {
            key: "reportStatus",
            field: "reportStatus",
            title: messages['scheduled_reports_status'],
            sort: true,
            show: true,
            resize: true,
            width: 150,
            export: true,
            code: "scheduled_reports_status",
            functionString: `
                let lang = "${lang}";
                const translations = {
                    en: {
                        completed: "Completed",
                        fail: "Fail",   
                    },
                    es: {
                        completed: "Completado",
                        fail: "Error",
                    }
                };

                let reportStatus = translations[lang] ? translations[lang][row?.reportStatus] : ""
                return reportStatus || row?.reportStatus
            `,
            render: (item) => <span>{messages[item?.reportStatus] || item?.reportStatus}</span>
        },
        {
            key: "error",
            field: "error",
            title: messages['scheduled_error'],
            sort: true,
            show: true,
            resize: true,
            width: 200,
            export: true,
            code: "scheduled_error",

            render: (item) => <span>{item?.error}</span>
        }
    ])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0} style={{ overflow: "auto" }}>
                <Grid
                    container
                    direction="row"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95vh' }}
                >
                    <Grid item xs={12} style={{ textAlign: 'left' }}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ marginBottom: 10 }}
                        >
                            <Grid item xs={8}>
                                <Typography variant="h5" component="div" style={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton
                                        onClick={() => {
                                            navigate(-1);
                                        }
                                        }
                                        size="large">
                                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon">
                                            arrow_back
                                        </Icon>
                                    </IconButton>
                                    <span style={{ marginRight: 5, opacity: 0.6 }}>{messages['report_overview_title']}:</span>
                                    Scheduled Reports
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider style={{ backgroundColor: '#999999' }} />
                            </Grid>
                        </Grid>

                        <DataView
                            name="scheduled-reports-main-v5"
                            exportName="scheduledreports"
                            entity="scheduled-reports"
                            graphqlFunction="searchReportLogs"
                            graphqlSort="ReportLogSortInput"
                            graphqlConditions="ReportLogSearchInput"
                            customQuery="id company { id, name }"
                            dinamicQuery
                            ref={tableRef}
                            columns={columns}
                            topheight={200}
                            noSplit
                            noPDF
                            disableItemSelection
                            noFilters
                            noSearch
                            noTags
                            noExcel
                            onChangeColumns={(items) => {
                                let currentColumns = items.map((item) => ({
                                    ...item,
                                    title: messages[item.code],
                                    render: item.field === "reportStatus" ? (item) => {
                                        return <span>{messages[item?.reportStatus] || item?.reportStatus}</span>
                                    } : item.field === "sendByEmail" ? (item) => {
                                        return <span>{item?.sendByEmail ? messages['scheduled_reports_yes'] : messages['scheduled_reports_no']}</span>
                                    } : item.render,
                                    functionString: item.key === "reportStatus" ? `
                                        let lang = "${lang}";
                                        const translations = {
                                            en: {
                                                completed: "Completed",
                                                fail: "Fail",   
                                            },
                                            es: {
                                                completed: "Completado",
                                                fail: "Error",
                                            }
                                        };

                                        let reportStatus = translations[lang] ? translations[lang][row?.reportStatus] : ""
                                        return reportStatus || row?.reportStatus
                                    ` : item.key === "sendByEmail" ? `
                                        let lang = "${lang}";
                                        const translations = {
                                            en: {
                                                yes: "Yes",
                                                no: "No",   
                                            },
                                            es: {
                                                yes: "Si",
                                                no: "No",
                                            }
                                        };
                                        return row?.sendByEmail ? translations[lang].yes : translations[lang].no
                                    `: item.functionString
                                }))
                                setColumns(currentColumns)
                            }}
                            onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                                let currentConditions = {}

                                if (!conditions && tags) {
                                    currentConditions = tableRef?.current?.conditions;
                                    currentTags = tags;
                                } else {
                                    currentConditions = conditions || [];
                                    let validateAccount = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "accountId");
                                    validateAccount && setAccount(validateAccount?.account);
                                    // Never restore a previously saved date range from preferences — this
                                    // branch only runs on the initial preferences restore (DataView passes
                                    // `conditions` as an array only in that case), so always reset the date
                                    // filter to "last 30 days" instead of reusing a stale saved range.
                                    currentConditions = currentConditions.filter((x) => !x?.range);
                                    let start = moment.utc(getRetentionMinDate().format("YYYY-MM-DD") + " 00:00:00").format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
                                    let end = moment.utc(moment(new Date()).format("YYYY-MM-DD") + " 23:59:59").format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
                                    currentConditions.push({
                                        fields: [], terms: [],
                                        range: {
                                            fields: ["createdOn"],
                                            GTE: start,
                                            LTE: end
                                        },
                                        exact: true, conditionOperator: "AND"
                                    })

                                    setStartDate(start);
                                    setEndDate(end);
                                }

                                if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                                else tableRef?.current?.getData(null, rows, sort, currentConditions);

                            }}
                            tableButtons={["export", 'orderColumn']}
                            defaultSort={{ field: "createdOn", order: "DESC" }}
                        />
                    </Grid>
                </Grid>
            </Paper>
            <AdvancedFilters
                setOpenPanel={setOpenPanel}
                openPanel={openPanel}
                account={account}
                setAccount={setAccount}
                tableRef={tableRef}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
            />
            {openModal && <Details />}
            {contactsOpenModal && <Contacts />}
        </Container>
    );
}
export default ScheduledReports;